import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TestimonialCardProps {
  testimonial: {
    id: number;
    customerName: string;
    location: string;
    rating: number;
    date: string;
    reviewText: string;
    puppyVariety: string;
    customerImage: string;
    customerImageAlt: string;
    puppyImage?: string;
    puppyImageAlt?: string;
    highlights: string[];
  };
}

const TestimonialCard = ({ testimonial }: TestimonialCardProps) => {
  return (
    <div className="rounded-xl bg-card p-6 shadow-warm transition-smooth hover:shadow-warm-md">
      {/* Customer Info */}
      <div className="mb-4 flex items-start gap-4">
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full">
          <AppImage
            src={testimonial.customerImage}
            alt={testimonial.customerImageAlt}
            width={56}
            height={56}
            quality={85}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-foreground">
            {testimonial.customerName}
          </h3>
          <p className="caption text-muted-foreground">{testimonial.location}</p>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, index) => (
                <Icon
                  key={index}
                  name="StarIcon"
                  size={16}
                  variant={index < testimonial.rating ? 'solid' : 'outline'}
                  className={
                    index < testimonial.rating
                      ? 'text-warning' :'text-muted-foreground/30'
                  }
                />
              ))}
            </div>
            <span className="caption text-muted-foreground">
              {testimonial.date}
            </span>
          </div>
        </div>
      </div>

      {/* Review Text */}
      <p className="mb-4 text-foreground leading-relaxed">
        {testimonial.reviewText}
      </p>

      {/* Highlights */}
      {testimonial.highlights.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {testimonial.highlights.map((highlight, index) => (
            <span
              key={index}
              className="rounded-full bg-primary/10 px-3 py-1 caption font-medium text-primary"
            >
              {highlight}
            </span>
          ))}
        </div>
      )}

      {/* Puppy Image */}
      {testimonial.puppyImage && testimonial.puppyImageAlt && (
        <div className="overflow-hidden rounded-lg">
          <AppImage
            src={testimonial.puppyImage}
            alt={testimonial.puppyImageAlt}
            width={600}
            height={400}
            quality={85}
            sizes="(max-width: 768px) 100vw, 600px"
            className="w-full"
          />
        </div>
      )}

      {/* Puppy Variety Badge */}
      <div className="mt-4 flex items-center gap-2">
        <Icon name="HeartIcon" size={16} variant="solid" className="text-accent" />
        <span className="caption font-medium text-muted-foreground">
          {testimonial.puppyVariety} Labrador
        </span>
      </div>
    </div>
  );
};

export default TestimonialCard;